import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { Lumber, PlateType, StandardConnectingHardwareModel, StockLengthPrice } from '@paragon/contract-library';
import { getLumberCode } from '@paragon/material-lists';

interface InventoryPrices {
  readonly platePricesPerPound: { readonly [key in PlateType]?: number };
  readonly platePriceRequests: { readonly [key in PlateType]?: PlateType };
  readonly lumberPrices: { readonly [key: string]: readonly StockLengthPrice[] | undefined }; // Price per one thousand board feet
  readonly lumberPriceRequests: { readonly [key: string]: string | undefined };
  readonly hangerPrices: { readonly [key in StandardConnectingHardwareModel]?: number };
  readonly hangerPriceRequests: { readonly [key in StandardConnectingHardwareModel]?: string };
}

const initialState: InventoryPrices = {
  platePricesPerPound: {},
  platePriceRequests: {},
  lumberPrices: {},
  lumberPriceRequests: {},
  hangerPrices: {},
  hangerPriceRequests: {},
};

const inventoryPricesSlice = createSlice({
  name: 'inventoryPrices',
  initialState,
  reducers: {
    setPricePerPoundForPlateType: (state, action: PayloadAction<{ plateType: PlateType; pricePerPound: number }>) => {
      state.platePricesPerPound[action.payload.plateType] = action.payload.pricePerPound;
    },
    setPlatePriceRequested: (state, action: PayloadAction<PlateType>) => {
      state.platePriceRequests[action.payload] = action.payload;
    },
    setPricesForLumber: (
      state,
      action: PayloadAction<{ lumber: Lumber; stockLengthPrices: readonly StockLengthPrice[] }>,
    ) => {
      state.lumberPrices[getLumberCode(action.payload.lumber)] = castDraft(action.payload.stockLengthPrices);
    },
    setLumberPriceRequested: (state, action: PayloadAction<Lumber>) => {
      const lumberPriceKey = getLumberCode(action.payload);
      state.lumberPriceRequests[lumberPriceKey] = lumberPriceKey;
    },
    setPriceForHanger: (state, action: PayloadAction<{ hanger: StandardConnectingHardwareModel; price: number }>) => {
      state.hangerPrices[action.payload.hanger] = action.payload.price;
    },
    setHangerPriceRequested: (state, action: PayloadAction<StandardConnectingHardwareModel>) => {
      state.hangerPriceRequests[action.payload] = action.payload;
    },
  },
});

export const {
  setPricePerPoundForPlateType,
  setPlatePriceRequested,
  setPricesForLumber,
  setLumberPriceRequested,
  setPriceForHanger,
  setHangerPriceRequested,
} = inventoryPricesSlice.actions;
export const inventoryPrices = inventoryPricesSlice.reducer;
